import { LOCATION_CHANGE } from 'react-router-redux'
import moment from 'moment-timezone'
import { selectItemById } from '../selectors/itemsSelector'
import { selectCategoryHierarchy, selectCategoryById } from '../selectors/categoriesSelector'
import { selectUserLoginStatusGA4 } from '../selectors/userSelector'

import {
  SET_LOGGED_IN_USER,
  LOGOUT_USER_SUCCESS,
  SET_NIGHT_MODE_SUCCESS,
} from '../user/userActions'
import {
  FETCH_NEXT_CATEGORY_ITEMS_SUCCESS,
  FETCH_NEXT_ITEMS_SUCCESS,
  FETCH_NEXT_TAG_ITEMS_SUCCESS,
  ITEM_CLICK,
  ITEM_SHARE_INTENT,
  ITEM_VOTE_SUCCESS,
} from '../components/item/itemsActions'


/**
 * All GA4 dataLayer variables
 */
const dataLayerVariables = [
  'dlv - article_language',
  'dlv - article_tags',
  'dlv - article_paywall',
  'dlv - event_value',
  'dlv - news_category1',
  'dlv - news_category2',
  'dlv - news_category3',
  'dlv - news_media',
  'dlv - news_url',
  'dlv - publish_date',
  'dlv - ui_element',
  'dlv - ab_test_variant',
  'dlv - login_status',
  'dlv - media_group',
]


/**
 * Return a "fresh" GA4 event where all data layer variables are explicitly set, so that
 * previously pushed values (from previous GA4 events) do not pollute the new event.
 */
function GA4Event(eventName, state, eventData) {
  const eventDataLayer = Object.assign({}, {
    event: 'sendGAEvent',
    'dlv - eventName': eventName,
    'dlv - login_status': selectUserLoginStatusGA4(state),
    // Enable once experiment is ready to go:
    //'dlv - ab_test_variant': selectUserABTestVariant(state),
  }, eventData)
  dataLayerVariables.forEach(variableName => {
    if (eventDataLayer[variableName] == null) {
      eventDataLayer[variableName] = undefined
    }
  })
  return eventDataLayer
}

/**
 * Reads state and creates GA4 data object for itemId
 */
function GetGA4ItemData(state, itemId) {
  const item = selectItemById(state, itemId)
  const tags = item.tags.toJS().join(',')
  const published = moment(item.timestamp * 1000)
  const url = item.custom ? '[custom feed, url redacted for privacy]' : item.link
  const paywall = item.paywalled ? 'paid' : 'free'

  let rootCategory = 'oma-syote'
  let subCategory = undefined
  let itemCategory = 'oma-syote'
  if (!item.custom) {
    const category = selectCategoryById(state, item.categoryId)
    const categories = selectCategoryHierarchy(state, category)
    rootCategory = categories.getIn([0, 'slug'])
    subCategory = categories.getIn([1, 'slug'], undefined)
    itemCategory = category.slug
  }

  return {
    'dlv - news_url': url,
    'dlv - news_media': item.source.toLowerCase(),
    'dlv - article_tags': tags,
    'dlv - news_category1': rootCategory,
    'dlv - news_category2': subCategory,
    'dlv - news_category3': itemCategory,
    'dlv - publish_date': published.format('DD/MM/YYYY'),
    'dlv - article_paywall': paywall,
    'dlv - article_language': 'fi', // No longer doing foreign news
    'dlv - media_group': item.mediaGroup || undefined,
  }
}



/**
 * Creates a middleware for pushing redux actions to GTM dataLayer (and from there to Google Analytics).
 */
export function getGtmMiddleware() {
  /**
   * Generate pageview whenever user navigates on the page. GA automatically logs the pageview on the
   * initial page load. Accordingly, we have to skip sending a pageview the first time this code runs.
   */
  let firstPageView = true
  const pushPageView = action => {
    if (firstPageView) {
      firstPageView = false
      return {}
    }
    return {
      event: 'react-pageview',
      pageview: {
        path: action.payload.pathname,
        title: 'Ampparit React Navigation',
      },
    }
  }

  /**
   * Set DataLayer variables 'userId' and 'userHash' on login or change, clear on logout
   */
  const pushUserId = (action, state) => {
    const userId = action.userId ? `${action.userId}` : ''
    const userHash = action.userHash ? `${action.userHash}` : ''
    const loginStatus = selectUserLoginStatusGA4(state)
    return { userId, userHash, 'dlv - login_status': loginStatus }
  }

  const pushNewsClickGA4 = (action, state) => {
    const itemData = GetGA4ItemData(state, action.id)
    itemData['dlv - ui_element'] = action.gaItemClickUiElement
    return GA4Event('news_click', state, itemData)
  }

  const pushNewsVoteGA4 = (action, state) => {
    const itemData = GetGA4ItemData(state, action.id)
    itemData['dlv - event_value'] = action.voteType
    return GA4Event('news_vote', state, itemData)
  }

  const pushLoadMoreNewsGA4 = (action, state) => {
    let list = '?'
    if (action.type === FETCH_NEXT_CATEGORY_ITEMS_SUCCESS) {
      list = action.category
    } else if (action.type === FETCH_NEXT_TAG_ITEMS_SUCCESS) {
      list = '#' + action.tag
    } else if (action.type === FETCH_NEXT_ITEMS_SUCCESS) {
      list = action.list
    }
    return GA4Event('load_more_news', state, { 'dlv - event_value': list })
  }

  const pushSetNightModeGA4 = (action, state) => {
    const value = action.active ? 'on' : 'off'
    return GA4Event('set_dark_mode', state, { 'dlv - event_value': value })
  }

  const pushNewsShareGA4 = (action, state) => {
    const itemData = GetGA4ItemData(state, action.id)
    itemData['dlv - event_value'] = action.channel
    return GA4Event('news_share', state, itemData)
  }

  /**
   * All redux actions we listen to and their associated data/event formatters
   */
  const eventsMap = {}
  eventsMap[ITEM_CLICK] = pushNewsClickGA4
  eventsMap[ITEM_VOTE_SUCCESS] = pushNewsVoteGA4
  eventsMap[FETCH_NEXT_CATEGORY_ITEMS_SUCCESS] = pushLoadMoreNewsGA4
  eventsMap[FETCH_NEXT_ITEMS_SUCCESS] = pushLoadMoreNewsGA4
  eventsMap[FETCH_NEXT_TAG_ITEMS_SUCCESS] = pushLoadMoreNewsGA4
  eventsMap[SET_NIGHT_MODE_SUCCESS] = pushSetNightModeGA4
  eventsMap[ITEM_SHARE_INTENT] = pushNewsShareGA4
  eventsMap[LOCATION_CHANGE] = pushPageView
  eventsMap[SET_LOGGED_IN_USER] = pushUserId
  eventsMap[LOGOUT_USER_SUCCESS] = pushUserId

  const middleware = store => next => action => {
    const result = next(action)
    if (eventsMap[action.type]) {
      const event = eventsMap[action.type](action, store.getState())
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(event)
    }
    return result
  }

  return middleware
}
